import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { legalBrand } from './legalBrand';

const TermsOfService = ({ brand }) => {
  const b = legalBrand(brand);
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <Link 
            to={b.home} 
            className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-4 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            {b.homeLabel}
          </Link>
          <h1 className="text-4xl font-light text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-gray-600">Last Updated: August 22, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8 md:p-12 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              By accessing and using this website https://bookofcolorz.com (the "Site"), you accept and agree to be bound by these Terms of Service 
              ("Terms"). If you do not agree to these Terms, please do not use this Site. These Terms apply to all visitors, users, and 
              others who access or use the Site.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Intellectual Property Rights</h2>
            
            <h3 className="text-xl font-medium text-gray-800 mb-3 mt-6">2.1 Copyright</h3>
            <div className="bg-purple-50 border-l-4 border-purple-600 p-6 rounded-r-lg">
              <p className="text-gray-800 leading-relaxed font-medium">
                Copyright © 2026 World of INSPIYAH. All Rights Reserved.
              </p>
              <p className="text-gray-700 leading-relaxed mt-3">
                Unauthorized reproduction, distribution, or adaptation of any material is strictly prohibited. This includes, but is not 
                limited to, all text, images, graphics, logos, digital assets, research materials, and any other content on this website 
                or associated platforms.
              </p>
            </div>

            <h3 className="text-xl font-medium text-gray-800 mb-3 mt-6">2.2 AI and Machine Learning Prohibition</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              No content from this Site may be:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Copied, scraped, indexed, downloaded, or ingested for any purpose</li>
              <li>Used for the training, development, or refinement of artificial intelligence systems</li>
              <li>Used for machine-learning models or automated datasets</li>
              <li>Used to generate derivative works through AI prompts, algorithms, or automated tools</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              This prohibition applies without exception unless explicit prior written permission is obtained.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mb-3 mt-6">2.3 Trademark</h3>
            <p className="text-gray-700 leading-relaxed">
              All trademarks, service marks, trade names, logos, and other proprietary designations ("Marks") displayed on this Site are 
              the property of World of INSPIYAH or their respective owners. You may not use any Marks without prior written permission.
            </p>
          </section>

          {/* User Conduct */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Conduct and Prohibited Activities</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You agree not to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Use the Site for any unlawful purpose or in violation of these Terms</li>
              <li>Copy, reproduce, distribute, or create derivative works from any content without permission</li>
              <li>Use automated systems (including robots, scrapers, or spiders) to access the Site</li>
              <li>Attempt to gain unauthorized access to any portion of the Site</li>
              <li>Interfere with or disrupt the Site or servers/networks connected to the Site</li>
              <li>Transmit viruses, malware, or any other malicious code</li>
              <li>Collect or harvest personal information of other users</li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>Engage in any activity that could damage, disable, or impair the Site</li>
            </ul>
          </section>

          {/* Licenses and Permissions */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Limited License</h2>
            <p className="text-gray-700 leading-relaxed">
              Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to 
              access and use the Site for personal, non-commercial purposes only. This license does not include:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
              <li>Any right to download, copy, or modify any content</li>
              <li>Any right to use content for commercial purposes</li>
              <li>Any right to create derivative works</li>
              <li>Any resale or commercial use of the Site or its contents</li>
            </ul>
          </section>

          {/* User Content */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. User-Generated Content</h2>
            <p className="text-gray-700 leading-relaxed">
              If you submit, post, or transmit any content to the Site ("User Content"), you represent and warrant that:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
              <li>You own or have the necessary rights to submit such content</li>
              <li>The content does not violate any third-party rights</li>
              <li>The content is not defamatory, obscene, or otherwise unlawful</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              By submitting User Content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, and display 
              such content in connection with operating the Site.
            </p>
          </section>

          {/* Disclaimer */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Disclaimer of Warranties</h2>
            <p className="text-gray-700 leading-relaxed">
              THE SITE AND ALL CONTENT ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, 
              INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. 
              WE DO NOT WARRANT THAT THE SITE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              TO THE FULLEST EXTENT PERMITTED BY LAW, WORLD OF INSPIYAH SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, 
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR OTHER INTANGIBLE LOSSES, 
              RESULTING FROM:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
              <li>Your access to or use of (or inability to access or use) the Site</li>
              <li>Any conduct or content of any third party on the Site</li>
              <li>Unauthorized access, use, or alteration of your content</li>
              <li>Any other matter relating to the Site</li>
            </ul>
          </section>

          {/* Indemnification */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to indemnify, defend, and hold harmless World of INSPIYAH, its affiliates, officers, directors, employees, and 
              agents from any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising out of:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
              <li>Your use of the Site</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Any User Content you submit or transmit</li>
            </ul>
          </section>

          {/* Enforcement */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Enforcement and Violations</h2>
            <p className="text-gray-700 leading-relaxed">
              Violations of these Terms, including unauthorized use of copyrighted material or AI training prohibition, will be prosecuted 
              to the fullest extent of the law. This includes pursuit of:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
              <li>Injunctive relief to prevent further violations</li>
              <li>Monetary damages under the Digital Millennium Copyright Act (DMCA)</li>
              <li>Statutory damages as permitted by law</li>
              <li>Attorneys' fees and costs</li>
            </ul>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Termination</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to terminate or suspend your access to the Site immediately, without prior notice or liability, for any 
              reason, including if you breach these Terms. Upon termination, your right to use the Site will immediately cease.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Governing Law and Jurisdiction</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of North Carolina, USA, without regard to its conflict 
              of law provisions. Any disputes arising from these Terms or your use of the Site shall be resolved in the courts of the State of North Carolina, USA.
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify or replace these Terms at any time at our sole discretion. We will provide notice of any 
              material changes by posting the new Terms on this page and updating the "Last Updated" date. Your continued use of the Site 
              after any changes constitutes acceptance of the new Terms.
            </p>
          </section>

          {/* Severability */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Severability</h2>
            <p className="text-gray-700 leading-relaxed">
              If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the 
              minimum extent necessary so that these Terms will otherwise remain in full force and effect.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For questions about these Terms, licensing inquiries, or to report violations, please contact us at:
            </p>
            <div className="mt-4 p-6 bg-gray-50 rounded-lg">
              <p className="text-gray-800 font-medium">World of INSPIYAH</p>
              <p className="text-gray-700">Email: {b.privacy}</p>
              <p className="text-gray-700">Address: North Carolina, USA</p>
              <p className="text-gray-700">Phone: Available upon request</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsOfService;